// ============================================================================
// LocationManageModal — add / edit / archive locations in the library.
// ============================================================================
// Opened from LocationLibrary's "Manage library" button. Mirrors the
// PresenterManageModal pattern.
// ============================================================================

import { useState } from 'react';
import { Pencil, Trash2, Plus, Loader2, Check, X, MapPin, ExternalLink } from 'lucide-react';
import { Modal } from './Modal';
import { api, type Location, type LocationInput } from '../api';
import { UploadPhoto } from './UploadPhoto';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  locations: Location[];
  onRefresh: () => void;
  /** Render as a flat in-flow panel (no overlay). Used by the admin sidebar. */
  inline?: boolean;
}

interface FormState {
  locationId: number | null;
  name: string;
  address: string;
  venueDetails: string;
  url: string;
  photoUrl: string;
  displayOrder: number;
}

const EMPTY_FORM: FormState = {
  locationId: null,
  name: '',
  address: '',
  venueDetails: '',
  url: '',
  photoUrl: '',
  displayOrder: 0,
};

export function LocationManageModal({ open, onClose, token, locations, onRefresh, inline = false }: Props) {
  const [form, setForm] = useState<FormState | null>(null);
  const [busyId, setBusyId] = useState<number | 'new' | null>(null);
  const [error, setError] = useState<string>('');

  const startNew = () => {
    setError('');
    setForm({ ...EMPTY_FORM, displayOrder: (locations.at(-1)?.display_order ?? 0) + 10 });
  };

  const startEdit = (l: Location) => {
    setError('');
    setForm({
      locationId: l.location_id,
      name: l.name,
      address: l.address ?? '',
      venueDetails: l.venue_details ?? '',
      url: l.url ?? '',
      photoUrl: l.photo_url ?? '',
      displayOrder: l.display_order,
    });
  };

  const cancelForm = () => { setForm(null); setError(''); };

  const save = async () => {
    if (!form) return;
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setError('');
    setBusyId(form.locationId ?? 'new');
    const payload: LocationInput = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      venue_details: form.venueDetails.trim() || null,
      url: form.url.trim() || null,
      photo_url: form.photoUrl.trim() || null,
      display_order: Number(form.displayOrder) || 0,
    };
    try {
      if (form.locationId == null) await api.createLocation(token, payload);
      else                          await api.updateLocation(token, form.locationId, payload);
      setForm(null);
      onRefresh();
    } catch (err: any) {
      setError(err?.message || 'Could not save location.');
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (l: Location) => {
    const yes = window.confirm(
      `Archive "${l.name}"? Past events keep their existing address text; future picks won't see it.`,
    );
    if (!yes) return;
    setBusyId(l.location_id);
    try {
      await api.archiveLocation(token, l.location_id);
      onRefresh();
    } catch (err: any) {
      alert(err?.message || 'Could not archive location.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage location library" maxWidth="max-w-3xl" inline={inline}>
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}

        {form == null && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={startNew}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark"
            >
              <Plus className="h-4 w-4" />
              Add new location
            </button>
          </div>
        )}

        {form != null && (
          <fieldset className="border border-uif-green/30 rounded-md p-4 space-y-3 bg-uif-green/5">
            <legend className="px-1 text-sm font-semibold text-uif-green-dark">
              {form.locationId == null ? 'New location' : 'Edit location'}
            </legend>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Name *</span>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Up in Farms Training Center"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Address</span>
              <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="2093 Lakeland Dr, Jackson, MS 39216"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Venue details</span>
              <textarea rows={3} value={form.venueDetails} onChange={(e) => setForm({ ...form, venueDetails: e.target.value })} placeholder="Parking notes, room layout, accessibility…"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">URL (map / venue website)</span>
              <input type="text" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://maps.google.com/?q=…"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>

            <div>
              <span className="block text-xs font-medium text-gray-700 mb-1">Photo</span>
              <UploadPhoto
                token={token}
                value={form.photoUrl}
                onChange={(url) => setForm({ ...form, photoUrl: url })}
                destination="locations"
                shape="banner"
              />
              <label className="block mt-2">
                <span className="block text-[11px] font-medium text-gray-500 mb-1">…or paste a URL manually</span>
                <input type="text" value={form.photoUrl} onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Display order</span>
              <input type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) || 0 })}
                className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={cancelForm} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
              <button type="button" onClick={save} disabled={busyId !== null}
                className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark disabled:bg-gray-300">
                {busyId !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {form.locationId == null ? 'Add location' : 'Save changes'}
              </button>
            </div>
          </fieldset>
        )}

        {form == null && (
          <div className="space-y-2">
            {locations.length === 0 && (
              <p className="text-sm text-gray-500 italic text-center py-4">
                No locations yet. Click "Add new location" above to start the library.
              </p>
            )}
            {locations.map((l) => (
              <div key={l.location_id} className="flex items-center gap-3 border border-gray-200 rounded-md p-3 hover:border-uif-green/40">
                <div className="h-12 w-12 rounded-md bg-uif-green/10 text-uif-green-dark flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">{l.name}</div>
                  {l.address && <div className="text-xs text-gray-600 truncate">{l.address}</div>}
                  {l.url && (
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-uif-green inline-flex items-center gap-1 hover:underline">
                      <ExternalLink className="h-3 w-3" /> Open
                    </a>
                  )}
                </div>
                <button type="button" onClick={() => startEdit(l)} disabled={busyId !== null}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button type="button" onClick={() => archive(l)} disabled={busyId !== null}
                  className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-3 w-3" /> Archive
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
